import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCategories } from '../../actions/categoryAction';
import { Box, Grid, Typography, styled, Divider } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import Loader from '../Layouts/Loader';

const CategoryItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2),
  transition: 'transform 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const ModernDivider = styled(Divider)(({ theme }) => ({
    margin: theme.spacing(2, 'auto'),
    width: '20%',
    height: '3px',
    background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: '5px',
}));

const AllCategoriesPage = () => {
    const dispatch = useDispatch();
    const { categoryName } = useParams();
    const { loading, categories } = useSelector((state) => state.categories);

    const [categoriesToShow, setCategoriesToShow] = useState([]);
    const [parentCategory, setParentCategory] = useState(null);

    useEffect(() => {
        dispatch(getCategories());
    }, [dispatch]);

    useEffect(() => {
        if (categories) {
            if (categoryName) {
                const findCategory = (cats, name) => {
                    if (!cats) return null;
                    for (const cat of cats) {
                        if (cat.name === name) {
                            return cat;
                        }
                        const found = findCategory(cat.children, name);
                        if (found) return found;
                    }
                    return null;
                }
                const category = findCategory(categories, categoryName);
                if (category) {
                    setParentCategory(category);
                    setCategoriesToShow(category.children || []);
                }
            } else {
                setParentCategory(null);
                setCategoriesToShow(categories);
            }
        }
    }, [categories, categoryName]);

    const getLink = (category) => {
        if (category.children && category.children.length > 0) {
            return `/categories/${category.name}`;
        }
        return `/search?category=${category.name}`;
    }

    return (
        <>
            {loading ? <Loader /> : (
                <Box
                    sx={{
                        minHeight: '70vh',
                        py: 5,
                    }}
                >
                    {parentCategory ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Link to={`/search?category=${parentCategory.name}`} style={{ textDecoration: 'none' }}>
                                <CategoryItem>
                                    <Box
                                        component="img"
                                        src={parentCategory.logo.url}
                                        alt={parentCategory.name}
                                        className="neon-glow"
                                        sx={{ height: 80, width: 80, objectFit: 'contain', mb: 1 }}
                                    />
                                    <Typography
                                        variant="h5"
                                        component="h1"
                                        sx={{ fontWeight: 'bold', color: 'text.primary', '&:hover': { color: 'primary.main' } }}
                                    >
                                        {parentCategory.name}
                                    </Typography>
                                </CategoryItem>
                            </Link>
                            <ModernDivider />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', textAlign: 'center' }}>All Categories</Typography>
                            <ModernDivider />
                        </Box>
                    )}
                    <Grid container spacing={3} justifyContent="center" sx={{ mt: 2 }}>
                        {categoriesToShow.length > 0 ? categoriesToShow.map((category, i) => (
                            <Grid item key={i}>
                                <Link to={getLink(category)} style={{ textDecoration: 'none' }}>
                                    <CategoryItem>
                                        <Box
                                            component="img"
                                            src={category.logo.url}
                                            alt={category.name}
                                            className="neon-glow"
                                            sx={{ height: 80, width: 80, objectFit: 'contain', mb: 1 }}
                                        />
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ color: 'text.primary', fontWeight: 500, '&:hover': { color: 'primary.main' } }}
                                        >
                                            {category.name}
                                        </Typography>
                                    </CategoryItem>
                                </Link>
                            </Grid>
                        )) : (
                            <Typography sx={{textAlign: 'center', width: '100%'}}>No subcategories found.</Typography>
                        )}
                    </Grid>
                </Box>
            )}
        </>
    );
};

export default AllCategoriesPage;